import { useEffect, useMemo, useState } from "react";
import {
  supabase,
  assignCompToDeal,
  assignCompsToDeal,
  saveRentRollRows,
  loadAssignedComps,
  loadRentRoll,
} from "../../supabase";
import { geocodeAddress } from "../../services/geocoding";
import { extractCompsFromFile } from "../../services/compExtraction";
import { compKey, rentRollKey, buildCompKeySet, buildRentRollKeySet, leaseTenantAddrKey } from "../../utils/compDedup";

// ─── Comp Extraction Staging UI ─────────────────────────────────────────────
// Modal that runs the AI extraction on a deal's uploaded document and stages
// the results for review before save. Per D5 — already-assigned comps are
// silently dropped from staging (not shown). New rows default to checked.
//
// On save:
//   - lease/sale rows that match an existing global `comps` row by address+date
//     → just create a deal_assigned_comps link (no duplicate global row)
//   - new lease/sale rows → INSERT into `comps` + create deal_assigned_comps
//   - rent roll rows → upsert into `deal_rent_roll` (dedup by subj+tenant+suite)

function fmtMoney(n) {
  if (n == null || n === 0) return "—";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${Math.round(n).toLocaleString()}`;
}
function fmtSF(n) { return n ? `${Number(n).toLocaleString()} SF` : "—"; }
function fmtRate(n) { return n != null && n !== 0 ? `$${Number(n).toFixed(2)}/SF` : "—"; }

const FIELD_LABEL_CLS = "block text-[10px] font-bold uppercase tracking-wider text-[#8a8a8a] mb-1";
function inputCls(darkMode) {
  return darkMode
    ? "text-xs px-2 py-1 rounded-md bg-[#242424] border border-[#3a3a3a] text-white focus:outline-none focus:border-pp-red"
    : "text-xs px-2 py-1 rounded-md bg-white border border-pp-gray text-pp-dark focus:outline-none focus:border-pp-red";
}

export default function CompExtractionStaging({
  open,
  onClose,
  onSaved,            // ({savedSale, savedLease, savedRentRoll}) => void
  dealId,
  dealName,
  fileName,           // string — for the header
  file,               // File object — what we run extraction against
  createdBy = null,
  darkMode = false,
}) {
  const [phase, setPhase] = useState("extracting");      // 'extracting' | 'staging' | 'saving' | 'error'
  const [progress, setProgress] = useState("");
  const [err, setErr] = useState(null);
  const [extracted, setExtracted] = useState({ sale_comps: [], lease_comps: [], rent_roll: [] });
  const [globalCompKeys, setGlobalCompKeys] = useState(new Set());          // compKey → true (whole library)
  const [globalCompByKey, setGlobalCompByKey] = useState(new Map());        // compKey → existing comp row (so we can link by id)
  const [alreadyAssignedKeys, setAlreadyAssignedKeys] = useState(new Set()); // compKey → true (skip silently)
  const [alreadyRentRollKeys, setAlreadyRentRollKeys] = useState(new Set());
  const [checked, setChecked] = useState({ sale: new Set(), lease: new Set(), rr: new Set() });

  // ── 1. Extract once on open ──────────────────────────────────────────
  useEffect(() => {
    if (!open || !file) return;
    let cancelled = false;
    setPhase("extracting"); setErr(null); setProgress("");
    (async () => {
      try {
        const result = await extractCompsFromFile(file, msg => { if (!cancelled) setProgress(msg); });
        if (cancelled) return;
        // Split lease comps into actual lease comps vs rent-roll rows. The
        // model uses `source` to flag rent-roll-origin rows; we keep them
        // separate downstream (they go to deal_rent_roll, not lease comps).
        const leaseRaw = (result.lease_comps || []).filter(r => r.source !== "rent_roll");
        const rentRollRaw = (result.lease_comps || []).filter(r => r.source === "rent_roll");
        // Map rent-roll rows into the deal_rent_roll schema
        const subj = result.subject_property || {};
        const subjAddr = [subj.address, subj.city, subj.state].filter(Boolean).join(", ");
        const rentRoll = rentRollRaw.map(r => ({
          subject_address: subjAddr || r.address,
          tenant: r.tenant,
          tenant_industry: r.tenant_industry || null,
          suite_id: r.suite_id || null,
          suite_sf: r.suite_sf || null,
          lease_rate: r.lease_rate || null,
          lease_type: r.lease_type || null,
          opex_psf: r.opex_psf ?? null,
          lease_start: r.lease_start || r.comp_date || null,
          lease_end: r.lease_end || null,
          walt_years: r.walt_years || null,
          notes: r.notes || null,
        }));
        // Cross-reference dedup: drop any lease-comp row whose (address,
        // tenant) matches a rent-roll row from this same extraction. Some
        // OMs cite their own subject tenants in the lease comps section,
        // which would otherwise create dupes. Rent roll wins.
        const rrCrossKeys = new Set(
          rentRoll
            .map(r => leaseTenantAddrKey({ address: r.subject_address, tenant: r.tenant }))
            .filter(Boolean)
        );
        const lease = leaseRaw.filter(l => {
          const k = leaseTenantAddrKey(l);
          return !k || !rrCrossKeys.has(k);
        });
        setExtracted({ sale_comps: result.sale_comps || [], lease_comps: lease, rent_roll: rentRoll });

        // ── 2. Fetch dedup context ─────────────────────────────────────
        setProgress("Checking for duplicates…");
        const [{ data: existingComps }, assignedComps, rentRollExisting] = await Promise.all([
          supabase.from("comps").select("id, address, comp_date, type").in("type", ["sale", "lease"]),
          loadAssignedComps(dealId),
          loadRentRoll(dealId),
        ]);
        const gMap = new Map();
        const gKeys = new Set();
        for (const c of (existingComps || [])) {
          const k = compKey({ address: c.address, comp_date: c.comp_date });
          gKeys.add(k);
          if (!gMap.has(k)) gMap.set(k, c);
        }
        setGlobalCompByKey(gMap);
        setGlobalCompKeys(gKeys);
        setAlreadyAssignedKeys(buildCompKeySet(assignedComps));
        setAlreadyRentRollKeys(buildRentRollKeySet(rentRollExisting));

        // Default checkbox state: every row checked EXCEPT those already
        // assigned to this deal (per D5 we skip those silently — i.e. they
        // never appear). We still need to filter the extracted lists.
        const assignedKeys = buildCompKeySet(assignedComps);
        const rrKeys = buildRentRollKeySet(rentRollExisting);
        // Tenant+address key of every rent-roll row already saved on this
        // deal — used to suppress lease-comp rows that would just duplicate
        // an existing rent-roll lease (e.g. extracting from a second doc
        // that re-cites the subject tenants in its comp section).
        const existingRRCrossKeys = new Set(
          (rentRollExisting || [])
            .map(r => leaseTenantAddrKey({ address: r.subject_address, tenant: r.tenant }))
            .filter(Boolean)
        );
        const visibleSale = (result.sale_comps || []).filter(c => !assignedKeys.has(compKey(c)));
        const visibleLease = lease.filter(c => {
          if (assignedKeys.has(compKey(c))) return false;
          const k = leaseTenantAddrKey(c);
          return !k || !existingRRCrossKeys.has(k);
        });
        const visibleRR = rentRoll.filter(r => !rrKeys.has(rentRollKey(r)));
        setExtracted({ sale_comps: visibleSale, lease_comps: visibleLease, rent_roll: visibleRR });

        setChecked({
          sale: new Set(visibleSale.map((_, i) => i)),
          lease: new Set(visibleLease.map((_, i) => i)),
          rr: new Set(visibleRR.map((_, i) => i)),
        });

        setPhase("staging");
      } catch (e) {
        if (cancelled) return;
        console.error("Extraction failed:", e);
        setErr(e.message || String(e));
        setPhase("error");
      }
    })();
    return () => { cancelled = true; };
  }, [open, file, dealId]);

  function toggle(group, idx) {
    setChecked(prev => {
      const next = { ...prev, [group]: new Set(prev[group]) };
      if (next[group].has(idx)) next[group].delete(idx); else next[group].add(idx);
      return next;
    });
  }

  function toggleAll(group, items) {
    setChecked(prev => {
      const all = items.every((_, i) => prev[group].has(i));
      const next = { ...prev, [group]: new Set(all ? [] : items.map((_, i) => i)) };
      return next;
    });
  }

  async function handleSave() {
    setPhase("saving");
    let savedSale = 0, savedLease = 0, savedRentRoll = 0;
    const failures = [];
    try {
      const insertedCompIds = [];
      const linkCompIds = [];

      // ── Sale + Lease comps: split into "new" vs "link to existing" ──
      // NOTE: only columns that currently exist in the `comps` table are
      // included. Extras like year_built / clear_height / micro_market /
      // status_detail aren't in the schema today; their values get folded
      // into `notes` so we don't lose context. Add them as real columns
      // later if you want them queryable.
      for (const groupName of ["sale", "lease"]) {
        const items = groupName === "sale" ? extracted.sale_comps : extracted.lease_comps;
        const set = checked[groupName];
        for (let i = 0; i < items.length; i++) {
          if (!set.has(i)) continue;
          const c = items[i];
          const k = compKey(c);
          const existing = globalCompByKey.get(k);
          if (existing) {
            linkCompIds.push(existing.id);
            if (groupName === "sale") savedSale++; else savedLease++;
            continue;
          }
          // New global comp — geocode + insert + assign
          const fullAddr = [c.address, c.city, c.state].filter(Boolean).join(", ");
          const coords = await geocodeAddress(fullAddr).catch(() => null);
          // year_built / clear_height / micro_market / status_detail were
          // added as real columns by 2026-05-29-add-comps-extra-columns.sql
          // — write them directly so they're queryable instead of being
          // smuggled through notes.
          const row = {
            type: groupName,
            source: fileName ? `Deal Extract: ${fileName}` : "Deal Extract",
            property_name: c.property_name || null,
            address: c.address,
            city: c.city || null,
            state: c.state || null,
            market: c.market || null,
            micro_market: c.micro_market || null,
            comp_date: c.comp_date || null,
            notes: c.notes || null,
            year_built: c.year_built || null,
            clear_height: c.clear_height || null,
            status_detail: c.status_detail || null,
            latitude: coords?.lat || null,
            longitude: coords?.lng || null,
            added_by: createdBy || "Deal Extract",
          };
          if (groupName === "sale") {
            Object.assign(row, {
              buyer: c.buyer || null,
              seller: c.seller || null,
              sale_price: c.sale_price || null,
              building_sf: c.building_sf || null,
              price_psf: c.price_psf || (c.sale_price && c.building_sf ? Math.round(c.sale_price / c.building_sf) : null),
              cap_rate: c.cap_rate || null,
            });
          } else {
            Object.assign(row, {
              tenant: c.tenant || null,
              tenant_industry: c.tenant_industry || null,
              building_sf: c.building_sf || null,
              suite_sf: c.suite_sf || null,
              lease_rate: c.lease_rate || null,
              lease_type: c.lease_type || null,
              opex_psf: c.opex_psf ?? null,
              landlord: c.landlord || null,
              loading_type: c.loading_type || null,
              door_type: c.door_type || null,
            });
          }
          const { data: ins, error: insErr } = await supabase.from("comps").insert([row]).select().single();
          if (insErr) {
            console.error("Insert global comp failed:", insErr, row);
            failures.push(`${groupName}: ${c.address || "(no address)"} — ${insErr.message}`);
            continue;
          }
          if (groupName === "sale") savedSale++; else savedLease++;
          insertedCompIds.push(ins.id);
        }
      }

      // ── Create deal_assigned_comps rows for both newly-inserted and linked ──
      const allCompIds = [...insertedCompIds, ...linkCompIds];
      if (allCompIds.length > 0) {
        const assignResult = await assignCompsToDeal(dealId, allCompIds, `extract:${fileName || "document"}`, createdBy);
        if (assignResult.error) {
          failures.push(`assign ${allCompIds.length} comp(s) to deal: ${assignResult.error}`);
          // Reset the per-type counts since none of the comps actually made
          // it into Deal Comp Set — they're orphans in the global library.
          savedSale = 0;
          savedLease = 0;
        }
      }

      // ── Rent roll rows ──
      const selectedRR = extracted.rent_roll.filter((_, i) => checked.rr.has(i)).map(r => ({
        ...r,
        source: `om_extract:${fileName || "document"}`,
        created_by: createdBy,
      }));
      const rrResult = await saveRentRollRows(dealId, selectedRR);
      savedRentRoll = rrResult.count;
      if (rrResult.error) {
        failures.push(`rent_roll (${selectedRR.length} row(s)): ${rrResult.error}`);
      }

      // Surface failures visibly so silent saves can't happen again.
      if (failures.length > 0) {
        window.alert(
          `Saved ${savedSale} sale + ${savedLease} lease + ${savedRentRoll} rent roll, but ${failures.length} row(s) failed:\n\n${failures.slice(0, 8).join("\n")}${failures.length > 8 ? `\n…and ${failures.length - 8} more (see console)` : ""}`
        );
      }
      onSaved?.({ savedSale, savedLease, savedRentRoll, failures });
      onClose?.();
    } catch (e) {
      console.error("Save extraction failed:", e);
      setErr(e.message || String(e));
      setPhase("error");
    }
  }

  if (!open) return null;

  const cls = {
    bg: darkMode ? "bg-[#242424]" : "bg-white",
    text: darkMode ? "text-white" : "text-pp-dark",
    muted: darkMode ? "text-[#8a8a8a]" : "text-[#8a8a8a]",
    border: darkMode ? "border-[#3a3a3a]" : "border-pp-gray",
    panel: darkMode ? "bg-[#2c2c2c]" : "bg-pp-light",
  };

  const totals = {
    sale: extracted.sale_comps.length,
    lease: extracted.lease_comps.length,
    rr: extracted.rent_roll.length,
  };
  const selTotals = {
    sale: checked.sale.size,
    lease: checked.lease.size,
    rr: checked.rr.size,
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-6" style={{ background: "rgba(0,0,0,0.55)" }} onClick={onClose}>
      <div className={`${cls.bg} ${cls.text} w-full max-w-5xl h-[85vh] rounded-xl shadow-2xl flex flex-col font-montserrat`} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className={`px-5 py-4 border-b ${cls.border} flex items-center justify-between flex-shrink-0`}>
          <div>
            <h2 className="text-lg font-bold">Extract from Document</h2>
            <p className={`text-xs ${cls.muted} mt-0.5`}>{fileName} → {dealName}</p>
          </div>
          <button onClick={onClose} className={`text-xl ${cls.muted} hover:${cls.text} px-2`}>✕</button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {phase === "extracting" && (
            <div className="p-10 text-center">
              <div className="text-sm font-semibold mb-2">{progress || "Extracting…"}</div>
              <div className={`text-xs ${cls.muted}`}>This can take 15–60 seconds for large OMs.</div>
            </div>
          )}

          {phase === "error" && (
            <div className="p-10 text-center">
              <div className="text-sm text-[#a5241b] font-semibold mb-2">Extraction failed</div>
              <div className={`text-xs ${cls.muted} max-w-md mx-auto`}>{err}</div>
            </div>
          )}

          {(phase === "staging" || phase === "saving") && (
            <div>
              {/* Sale Comps */}
              <ExtractionSection
                title="Sale Comps"
                color="#c8922f"
                items={extracted.sale_comps}
                checkedSet={checked.sale}
                onToggle={i => toggle("sale", i)}
                onToggleAll={() => toggleAll("sale", extracted.sale_comps)}
                renderRow={c => (
                  <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs">
                    {c.market && <span className={cls.muted}>{c.market}</span>}
                    {c.building_sf && <span>{fmtSF(c.building_sf)}</span>}
                    {c.sale_price && <span>{fmtMoney(c.sale_price)}</span>}
                    {c.price_psf && <span>${Number(c.price_psf).toFixed(0)}/SF</span>}
                    {c.cap_rate && <span>{Number(c.cap_rate).toFixed(2)}% cap</span>}
                    {c.comp_date && <span className={cls.muted}>{c.comp_date}</span>}
                  </div>
                )}
                globalCompByKey={globalCompByKey}
                cls={cls}
              />

              {/* Lease Comps */}
              <ExtractionSection
                title="Lease Comps"
                color="#b4b4b4"
                items={extracted.lease_comps}
                checkedSet={checked.lease}
                onToggle={i => toggle("lease", i)}
                onToggleAll={() => toggleAll("lease", extracted.lease_comps)}
                renderRow={c => (
                  <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs">
                    {c.tenant && <span className="font-medium">{c.tenant}</span>}
                    {c.suite_sf && <span>{fmtSF(c.suite_sf)}</span>}
                    {c.lease_rate && <span>{fmtRate(c.lease_rate)}</span>}
                    {c.lease_type && <span className={cls.muted}>{c.lease_type}</span>}
                    {c.comp_date && <span className={cls.muted}>{c.comp_date}</span>}
                  </div>
                )}
                globalCompByKey={globalCompByKey}
                cls={cls}
              />

              {/* Rent Roll */}
              <ExtractionSection
                title="Rent Roll"
                color="#4f9d76"
                items={extracted.rent_roll}
                checkedSet={checked.rr}
                onToggle={i => toggle("rr", i)}
                onToggleAll={() => toggleAll("rr", extracted.rent_roll)}
                renderRow={r => (
                  <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs">
                    <span className="font-medium">{r.tenant}</span>
                    {r.suite_id && <span className={cls.muted}>{r.suite_id}</span>}
                    {r.suite_sf && <span>{fmtSF(r.suite_sf)}</span>}
                    {r.lease_rate && <span>{fmtRate(r.lease_rate)}</span>}
                    {r.lease_type && <span className={cls.muted}>{r.lease_type}</span>}
                    {r.lease_start && <span className={cls.muted}>{r.lease_start} → {r.lease_end || "?"}</span>}
                  </div>
                )}
                renderHeader={r => r.subject_address || r.tenant}
                isRentRoll
                cls={cls}
              />

              {totals.sale + totals.lease + totals.rr === 0 && (
                <div className={`p-10 text-center text-sm ${cls.muted}`}>
                  No new comps or rent roll rows extracted from this document. Anything that was extracted is already assigned to this deal.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`px-5 py-3 border-t ${cls.border} flex items-center justify-between flex-shrink-0`}>
          <span className={`text-xs ${cls.muted}`}>
            {phase === "staging" && (selTotals.sale + selTotals.lease + selTotals.rr > 0
              ? `Will save: ${selTotals.sale} sale · ${selTotals.lease} lease · ${selTotals.rr} rent roll`
              : "Pick at least one row to save")}
          </span>
          <div className="flex gap-2">
            <button onClick={onClose} className={`text-xs px-4 py-2 rounded-lg border ${cls.border} ${cls.muted} hover:${cls.text}`}>Cancel</button>
            <button
              onClick={handleSave}
              disabled={phase !== "staging" || (selTotals.sale + selTotals.lease + selTotals.rr === 0)}
              style={{ color: "#ffffff" }}
              className="text-xs px-4 py-2 rounded-lg bg-pp-red hover:bg-pp-red-light font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {phase === "saving" ? "Saving…" : "Save Selected"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ExtractionSection({ title, color, items, checkedSet, onToggle, onToggleAll, renderRow, renderHeader, isRentRoll, globalCompByKey, cls }) {
  const allChecked = items.length > 0 && items.every((_, i) => checkedSet.has(i));
  if (items.length === 0) return null;
  return (
    <div className={`border-b ${cls.border}`}>
      <div className={`px-5 py-2 ${cls.panel} flex items-center gap-3`}>
        <input type="checkbox" checked={allChecked} onChange={onToggleAll} className="accent-pp-red" />
        <span className="text-xs font-bold uppercase tracking-wider" style={{ color }}>{title}</span>
        <span className={`text-[10px] ${cls.muted}`}>{items.length} extracted · {checkedSet.size} selected</span>
      </div>
      {items.map((item, idx) => {
        const checked = checkedSet.has(idx);
        const willLink = !isRentRoll && globalCompByKey?.has(compKey(item));
        return (
          <label key={idx} className="flex items-start gap-3 px-5 py-2 cursor-pointer" style={{ background: checked ? (cls.bg === "bg-[#242424]" ? "rgba(233,53,41,0.06)" : "rgba(233,53,41,0.04)") : undefined }}>
            <input type="checkbox" checked={checked} onChange={() => onToggle(idx)} className="mt-1 accent-pp-red" />
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-sm font-semibold truncate">{renderHeader ? renderHeader(item) : (item.property_name || item.address?.split(",")[0])}</span>
                {willLink && (
                  <span className="text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded bg-[#ebebeb] text-[#555555]">🔗 LINK TO EXISTING</span>
                )}
                {!willLink && !isRentRoll && (
                  <span className="text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded bg-[rgba(79,157,118,0.16)] text-[#3a7e5c]">🆕 NEW</span>
                )}
              </div>
              {!isRentRoll && item.address && <div className={`text-xs ${cls.muted} truncate`}>{item.address}</div>}
              <div className="mt-0.5">{renderRow(item)}</div>
            </div>
          </label>
        );
      })}
    </div>
  );
}
